import React, { useContext, useEffect, useMemo, useState } from "react";
import "./CommonIntroduction.scss";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { MemberContext } from "../Contexts/MemberContext";

const TennisRelated = () => {
  const { currentUserInfo } = useContext(MemberContext);
  const isAdmin = currentUserInfo?.administrator === true;

  const precomponentName = "TennisRelated";
  const componentName = precomponentName.toLowerCase();
  const slide = "テニス等のサークル活動支援";
  const navigate = useNavigate();

  // =========================
  // state
  // =========================
  const [targetDataList, setTargetDataList] = useState([]);
  const [columns, setColumns] = useState(null); // ★ null 初期化

  // =========================
  // Firestore 取得 + ソート
  // =========================
  useEffect(() => {
    const fetchTargetData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, componentName));

        const list = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            eventdate:
              data.eventdate instanceof Timestamp
                ? data.eventdate.toDate().toISOString()
                : data.eventdate,
            created_at:
              data.created_at instanceof Timestamp
                ? data.created_at.toDate().toLocaleString()
                : data.created_at,
            updated_at:
              data.updated_at instanceof Timestamp
                ? data.updated_at.toLocaleString()
                : data.updated_at,
          };
        });

        // eventdate 昇順（古 → 新）
        list.sort((a, b) => new Date(a.eventdate) - new Date(b.eventdate));

        setTargetDataList(list);
      } catch (error) {
        console.error("データ取得エラー:", error);
      }
    };

    fetchTargetData();
  }, [componentName]);

  // =========================
  // レスポンシブ列数制御
  // =========================
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      let newColumns;

      if (width >= 1600) newColumns = 4;
      else if (width >= 1200) newColumns = 3;
      else if (width >= 650) newColumns = 2;
      else newColumns = 1;

      setColumns(newColumns);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // =========================
  // ダミー要素補充
  // =========================
  const fillDummyItems = (arr, multiple) => {
    const remainder = arr.length % multiple;
    if (remainder === 0) return arr;

    const dummyCount = multiple - remainder;
    const dummyItems = Array.from({ length: dummyCount }, () => ({
      isDummy: true,
    }));

    return [...arr, ...dummyItems];
  };

  // =========================
  // ★ 最大の修正点
  // =========================
  const adjustedArray = useMemo(() => {
    if (!columns || targetDataList.length === 0) return [];
    return fillDummyItems(targetDataList, columns);
  }, [targetDataList, columns]);

  // =========================
  // 初期描画ガード
  // =========================
  if (!columns) return null; // or ローディングUI

  // =========================
  // JSX
  // =========================
  return (
    <div className="commonintroduction-container">
      <video
        autoPlay
        loop
        muted
        className="commonintroduction-container-background-video"
      >
        <source src="/videos/background.mp4" type="video/mp4" />
      </video>

      <div className="commonintroduction-content">
        <div className="commonintroduction-title">
          <div className="commonintroduction-titletext">
            {slide.split("").map((char, i) => (
              <span
                key={i}
                className={`commonintroduction-title-char ${
                  char === " " ? "whitespace" : ""
                }`}
                style={{ "--char-index": i }}
              >
                {char}
              </span>
            ))}
          </div>

          {isAdmin && (
            <div
              className="commonintroduction-post"
              onClick={() =>
                navigate("/firestoreservice", {
                  state: {
                    posttitle: "テニス関係情報",
                    databasename: componentName,
                    targetitems: [
                      "order",
                      "eventday",
                      "title",
                      "content",
                      "linkUrl",
                      "youtubeUrl",
                      "image",
                    ],
                  },
                })
              }
            >
              <span className="commonintroduction-post-text">追 加</span>
            </div>
          )}
        </div>

        <div className="commonintroduction-box-wrap">
          {adjustedArray.map((item, index) => (
            <div
              key={index}
              className={`commonintroduction-box ${
                item.isDummy ? "dummy" : ""
              }`}
            >
              {!item.isDummy && (
                <>
                  <div className="commonintroduction-box-title">
                    <span className="commonintroduction-box-title-text">
                      {item.title}
                    </span>

                    {isAdmin && (
                      <button
                        className="commonintroduction-box-edit-button"
                        onClick={() =>
                          navigate(`/firestoreservice/${item.id}/edit`, {
                            state: {
                              posttitle: "テニス関係情報",
                              databasename: componentName,
                              targetitems: [
                                "order",
                                "eventday",
                                "title",
                                "content",
                                "linkUrl",
                                "youtubeUrl",
                                "image",
                              ],
                              targetData: item,
                            },
                          })
                        }
                      >
                        ︙
                      </button>
                    )}
                  </div>

                  <div
                    className="commonintroduction-box-content"
                    onClick={() =>
                      item.linkUrl && (window.location.href = item.linkUrl)
                    }
                  >
                    {item.image && (
                      <img
                        src={item.image}
                        className="commonintroduction-box-image"
                        alt="テニス活動イメージ"
                      />
                    )}

                    <span className="commonintroduction-box-content-text">
                      {item.content}
                    </span>
                  </div>

                  <div className="commonintroduction-box-button-case">
                    <div
                      className="commonintroduction-box-button"
                      onClick={() =>
                        item.linkUrl && (window.location.href = item.linkUrl)
                      }
                    >
                      <span className="commonintroduction-box-button-text">
                        具体例を確認
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TennisRelated;
